import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { createChart, ColorType, IChartApi, Time } from 'lightweight-charts';
import { 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  DollarSign, 
  BarChart3, 
  AlertTriangle,
  RefreshCw,
  Maximize2
} from 'lucide-react';
import { fetchFundingRates, fetchLiquidations, fetchOpenInterest, fetchLongShortRatio } from '../lib/coinglass';
import { FundingRateSimple, LiquidationData, OpenInterestData, LongShortRatioData } from '../types/coinglass';
import { FullScreenModal } from './ui/FullScreenModal';

interface CombinedMarketData {
  fundingRates: FundingRateSimple[];
  liquidations: LiquidationData[];
  openInterest: OpenInterestData[];
  longShortRatio: LongShortRatioData | null;
  timestamp: number;
}

interface MetricCardProps {
  title: string;
  value: string;
  change?: number;
  icon: React.ReactNode;
  color: string;
  isLoading?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, icon, color, isLoading }) => {
  return (
    <motion.div
      className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-lg p-4"
      whileHover={{ scale: 1.02 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg bg-gradient-to-br ${color}`}>
          {icon}
        </div>
        {change !== undefined && (
          <div className={`flex items-center text-sm ${change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {change >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
            {Math.abs(change).toFixed(2)}%
          </div>
        )}
      </div>
      <div className="text-sm text-gray-400 mb-1">{title}</div>
      <div className="text-xl font-bold text-white">
        {isLoading ? (
          <div className="h-6 w-20 bg-zinc-700 rounded animate-pulse" />
        ) : (
          value
        )}
      </div>
    </motion.div>
  );
};

interface PriceTickerProps {
  symbol: string;
  price: number;
  change24h: number;
}

const PriceTicker: React.FC<PriceTickerProps> = ({ symbol, price, change24h }) => {
  return (
    <motion.div
      className="flex items-center space-x-4 bg-zinc-900/80 backdrop-blur border border-zinc-800 rounded-lg p-3"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
    >
      <div className="flex items-center space-x-2">
        <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
          {symbol[0]}
        </div>
        <div>
          <div className="font-bold text-white">{symbol}</div>
          <div className="text-xs text-gray-400">Perpetual</div>
        </div>
      </div>
      <div className="flex-1 text-right">
        <div className="text-lg font-bold text-white">
          ${price.toLocaleString()}
        </div>
        <div className={`text-sm flex items-center justify-end ${change24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
          {change24h >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
          {change24h.toFixed(2)}%
        </div>
      </div>
    </motion.div>
  );
};

const AdvancedTradingDashboard: React.FC = () => {
  const [selectedSymbol, setSelectedSymbol] = useState('BTC');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeTimeframe, setActiveTimeframe] = useState('4h');
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);

  // Fetch combined market data
  const { data: marketData, error, isLoading, refetch } = useQuery<CombinedMarketData>({
    queryKey: ['advancedMarketData', selectedSymbol],
    queryFn: async () => {
      const [fundingRates, liquidations, openInterest, longShortRatio] = await Promise.all([
        fetchFundingRates(selectedSymbol),
        fetchLiquidations(),
        fetchOpenInterest(selectedSymbol),
        fetchLongShortRatio('Binance', `${selectedSymbol}USDT`)
      ]);

      return {
        fundingRates,
        liquidations: liquidations.filter(l => l.symbol.toUpperCase() === selectedSymbol.toUpperCase()),
        openInterest,
        longShortRatio,
        timestamp: Date.now()
      };
    },
    refetchInterval: 30000, // 30 seconds
  });

  // Mock price data - in real app would come from API
  const mockPriceData = {
    BTC: { price: 43250, change24h: 2.45 },
    ETH: { price: 2650, change24h: -1.23 },
    SOL: { price: 98.5, change24h: 4.12 }
  };

  useEffect(() => {
    if (!chartContainerRef.current || !marketData?.openInterest?.length) return;

    // Create advanced chart with multiple series
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#9ca3af',
      },
      width: chartContainerRef.current.clientWidth,
      height: isFullScreen ? window.innerHeight - 200 : 400,
      grid: {
        vertLines: { color: '#27272a' },
        horzLines: { color: '#27272a' },
      },
      rightPriceScale: {
        borderColor: '#71717a',
      },
      timeScale: {
        borderColor: '#71717a',
        timeVisible: true,
        secondsVisible: false,
      },
    });

    chartRef.current = chart;

    // Open Interest Line Series
    const openInterestSeries = chart.addLineSeries({
      color: '#eab308',
      lineWidth: 2,
      priceScaleId: 'left',
    });

    // Mock chart data based on open interest
    const chartData = marketData.openInterest.slice(0, 50).map((item, index) => ({
      time: (Date.now() - (50 - index) * 4 * 60 * 60 * 1000) / 1000 as Time,
      value: item.openInterest || Math.random() * 1000000000
    }));

    openInterestSeries.setData(chartData);

    // Funding Rate Histogram (as area series)
    if (marketData.fundingRates.length > 0) {
      const fundingAreaSeries = chart.addAreaSeries({
        lineColor: '#8b5cf6',
        topColor: 'rgba(139, 92, 246, 0.3)',
        bottomColor: 'rgba(139, 92, 246, 0)',
        lineWidth: 1,
        priceScaleId: 'right',
      });      const fundingData = chartData.map((item) => ({
        time: item.time,
        value: (marketData.fundingRates[0]?.fundingRate || 0) * 100 + Math.random() * 0.1 - 0.05
      }));

      fundingAreaSeries.setData(fundingData);
    }

    chart.timeScale().fitContent();

    const handleResize = () => {
      chart.applyOptions({ 
        width: chartContainerRef.current?.clientWidth || 800 
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [marketData, isFullScreen]);

  const calculateMetrics = () => {
    if (!marketData) return null;

    const totalLiquidations = marketData.liquidations.reduce((sum, item) => 
      sum + item.liquidation_usd_24h, 0
    );

    const avgFundingRate = marketData.fundingRates.length > 0 
      ? marketData.fundingRates.reduce((sum, item) => sum + item.fundingRate, 0) / marketData.fundingRates.length
      : 0;

    const totalOpenInterest = marketData.openInterest.reduce((sum, item) => 
      sum + (item.openInterest || 0), 0
    );

    return {
      totalLiquidations,
      avgFundingRate,
      totalOpenInterest,
      longShortRatio: marketData.longShortRatio?.longRatio || 0
    };
  };

  const metrics = calculateMetrics();

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(4)}%`;
  };

  return (
    <div className="space-y-6">
      {/* Header with Symbol Selection */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Advanced Trading Dashboard</h2>
          <p className="text-gray-400">Real-time market data and analytics</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedSymbol}
            onChange={(e) => setSelectedSymbol(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white"
          >
            <option value="BTC">Bitcoin (BTC)</option>
            <option value="ETH">Ethereum (ETH)</option>
            <option value="SOL">Solana (SOL)</option>
          </select>
          <button
            onClick={() => refetch()}
            className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {/* Price Ticker */}
      <PriceTicker
        symbol={selectedSymbol}
        price={mockPriceData[selectedSymbol as keyof typeof mockPriceData].price}
        change24h={mockPriceData[selectedSymbol as keyof typeof mockPriceData].change24h}
      />

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Liquidations (24h)"
          value={formatUSD(metrics?.totalLiquidations || 0)}
          change={Math.random() * 20 - 10}
          icon={<AlertTriangle className="w-5 h-5 text-white" />}
          color="from-red-500 to-rose-600"
          isLoading={isLoading}
        />
        <MetricCard
          title="Avg Funding Rate"
          value={formatPercentage(metrics?.avgFundingRate || 0)}
          change={Math.random() * 4 - 2}
          icon={<DollarSign className="w-5 h-5 text-white" />}
          color="from-green-500 to-emerald-600"
          isLoading={isLoading}
        />
        <MetricCard
          title="Open Interest"
          value={formatUSD(metrics?.totalOpenInterest || 0)}
          change={Math.random() * 8 - 4}
          icon={<BarChart3 className="w-5 h-5 text-white" />}
          color="from-blue-500 to-cyan-600"
          isLoading={isLoading}
        />
        <MetricCard
          title="Long/Short Ratio"
          value={`${(metrics?.longShortRatio || 0).toFixed(2)}`}
          change={Math.random() * 6 - 3}
          icon={<Activity className="w-5 h-5 text-white" />}
          color="from-purple-500 to-pink-600"
          isLoading={isLoading}
        />
      </div>

      {/* Advanced Chart */}
      <motion.div
        className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-lg p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Market Analytics</h3>
            <p className="text-sm text-gray-400">Open Interest & Funding Rate Trends</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex bg-zinc-800 rounded-lg p-1">
              {['1h', '4h', '1d', '1w'].map((timeframe) => (
                <button
                  key={timeframe}
                  onClick={() => setActiveTimeframe(timeframe)}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    activeTimeframe === timeframe
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {timeframe}
                </button>
              ))}
            </div>
            <button
              onClick={() => setIsFullScreen(true)}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div ref={chartContainerRef} className="w-full" />

        {/* Chart Legend */}
        <div className="flex items-center justify-center space-x-6 mt-4 text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2" />
            <span className="text-gray-400">Open Interest</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-purple-500 rounded-full mr-2" />
            <span className="text-gray-400">Funding Rate</span>
          </div>
        </div>
      </motion.div>

      {/* Error State */}
      {error && (
        <motion.div
          className="bg-red-900/20 border border-red-800 rounded-lg p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-400">Failed to load market data</span>
          </div>
        </motion.div>
      )}

      {/* Full Screen Chart Modal */}
      <FullScreenModal
        isOpen={isFullScreen}
        onClose={() => setIsFullScreen(false)}
        title={`${selectedSymbol} Advanced Chart`}
      >
        <div ref={chartContainerRef} className="w-full h-full" />
      </FullScreenModal>
    </div>
  );
};

export default React.memo(AdvancedTradingDashboard);
